import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { WebsiteAnalyticsService } from '@/services/website-analytics.service';
import { ConversionFunnel as ConversionFunnelType, FunnelMetrics } from '@/models/website-analytics.model';
import { subDays } from 'date-fns';
import { FunnelForm } from './FunnelForm';

export const ConversionFunnel: React.FC = () => {
  const { profile } = useAuth();
  const [funnels, setFunnels] = useState<ConversionFunnelType[]>([]);
  const [selectedFunnel, setSelectedFunnel] = useState<ConversionFunnelType | null>(null);
  const [metrics, setMetrics] = useState<FunnelMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingFunnel, setEditingFunnel] = useState<ConversionFunnelType | null>(null);

  const loadFunnels = useCallback(async () => {
    setLoading(true);
    try {
      const data = await WebsiteAnalyticsService.getAllFunnels();
      setFunnels(data);
      if (data.length > 0 && !selectedFunnel) {
        setSelectedFunnel(data[0]);
      }
    } catch (error: any) {
      console.error('Error loading funnels:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedFunnel]);

  const loadFunnelMetrics = useCallback(async (funnelId: string) => {
    // This would calculate funnel metrics based on visitor data
    // For now, we'll create a placeholder structure
    try {
      // In production, this would query visitor data and calculate funnel steps
      // const visitors = await WebsiteAnalyticsService.getVisitorsInDateRange(...);
      // const funnelMetrics = calculateFunnelMetrics(visitors, funnel);
      
      // Placeholder metrics
      const placeholderMetrics: FunnelMetrics = {
        funnelId,
        funnelName: selectedFunnel?.name || 'Funnel',
        period: {
          start: subDays(new Date(), 30),
          end: new Date(),
        },
        totalEntered: 1000,
        steps: selectedFunnel?.steps.map((step, idx) => ({
          stepId: step.id,
          stepName: step.name,
          visitors: Math.round(1000 * Math.pow(0.7, idx)),
          dropOff: idx === 0 ? 0 : Math.round(1000 * (Math.pow(0.7, idx - 1) - Math.pow(0.7, idx))),
          dropOffRate: idx === 0 ? 0 : 30,
          conversionRate: (Math.round(1000 * Math.pow(0.7, idx)) / 1000) * 100,
          avgTimeToStep: idx * 60, // seconds
        })) || [],
        totalConversions: selectedFunnel?.steps.filter(s => s.isConversion).length ? 100 : 0,
        overallConversionRate: 10,
        avgTimeToConversion: 300,
      };
      
      setMetrics(placeholderMetrics);
    } catch (error: any) {
      console.error('Error loading funnel metrics:', error);
    }
  }, [selectedFunnel]);

  useEffect(() => {
    if (profile) {
      loadFunnels();
    }
  }, [profile, loadFunnels]);

  useEffect(() => {
    if (selectedFunnel) {
      loadFunnelMetrics(selectedFunnel.id);
    }
  }, [selectedFunnel, loadFunnelMetrics]);

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-2 text-gray-600">Loading conversion funnels...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Conversion Funnel Analysis</h2>
          <p className="mt-1 text-sm text-gray-500">
            Track visitor progression through your marketing funnel
          </p>
        </div>
        <button
          onClick={() => {
            setEditingFunnel(null);
            setShowForm(true);
          }}
          className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 text-sm font-medium"
        >
          + Create Funnel
        </button>
      </div>

      {/* Funnel Selector */}
      {funnels.length > 0 && (
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700">
              Select Funnel
            </label>
            <button
              onClick={() => {
                setEditingFunnel(selectedFunnel);
                setShowForm(true);
              }}
              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              Edit Funnel
            </button>
          </div>
          <select
            value={selectedFunnel?.id || ''}
            onChange={(e) => {
              const funnel = funnels.find(f => f.id === e.target.value);
              setSelectedFunnel(funnel || null);
            }}
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          >
            {funnels.map((funnel) => (
              <option key={funnel.id} value={funnel.id}>
                {funnel.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Funnel Visualization */}
      {selectedFunnel && metrics && (
        <div className="space-y-6">
          {/* Summary Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-600">Total Entrants</div>
              <div className="text-2xl font-bold text-gray-900">{metrics.totalEntered.toLocaleString()}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-600">Total Conversions</div>
              <div className="text-2xl font-bold text-green-600">{metrics.totalConversions.toLocaleString()}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-600">Conversion Rate</div>
              <div className="text-2xl font-bold text-blue-600">{metrics.overallConversionRate.toFixed(2)}%</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-600">Avg Time to Convert</div>
              <div className="text-2xl font-bold text-purple-600">
                {Math.round(metrics.avgTimeToConversion / 60)}m
              </div>
            </div>
          </div>

          {/* Funnel Steps */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Funnel Steps</h3>
            <div className="space-y-4">
              {metrics.steps.map((step, idx) => {
                const widthPercent = (step.visitors / metrics.totalEntered) * 100;
                const dropOffPercent = idx > 0 ? ((metrics.steps[idx - 1].visitors - step.visitors) / metrics.steps[idx - 1].visitors) * 100 : 0;
                
                return (
                  <div key={step.stepId} className="relative">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center">
                        <span className="text-sm font-medium text-gray-900">{idx + 1}. {step.stepName}</span>
                        {selectedFunnel?.steps.find(s => s.id === step.stepId)?.isConversion && (
                          <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                            Conversion
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-600">
                        {step.visitors.toLocaleString()} visitors ({step.conversionRate.toFixed(1)}%)
                      </div>
                    </div>
                    
                    {/* Funnel Bar */}
                    <div className="relative h-12 bg-gray-100 rounded-lg overflow-hidden">
                      <div
                        className={`h-full rounded-lg transition-all ${
                          selectedFunnel?.steps.find(s => s.id === step.stepId)?.isConversion ? 'bg-green-500' : 'bg-blue-500'
                        }`}
                        style={{ width: `${widthPercent}%` }}
                      >
                        <div className="absolute inset-0 flex items-center justify-center text-white font-medium text-sm">
                          {step.visitors.toLocaleString()}
                        </div>
                      </div>
                    </div>
                    
                    {/* Drop-off Info */}
                    {idx > 0 && dropOffPercent > 0 && (
                      <div className="mt-2 text-xs text-red-600">
                        {step.dropOff.toLocaleString()} dropped off ({dropOffPercent.toFixed(1)}%)
                      </div>
                    )}
                    
                    {/* Step Details */}
                    <div className="mt-2 flex space-x-4 text-xs text-gray-500">
                      <span>Avg time: {Math.round(step.avgTimeToStep / 60)}m</span>
                      {step.dropOffRate > 0 && (
                        <span>Drop-off rate: {step.dropOffRate.toFixed(1)}%</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Funnel Steps Table */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Detailed Metrics</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Step
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Visitors
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Drop-off
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Drop-off Rate
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Conversion Rate
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Avg Time
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {metrics.steps.map((step, idx) => (
                    <tr key={step.stepId}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {idx + 1}. {step.stepName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {step.visitors.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600">
                        {step.dropOff.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {step.dropOffRate.toFixed(1)}%
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                        {step.conversionRate.toFixed(1)}%
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {Math.round(step.avgTimeToStep / 60)}m {step.avgTimeToStep % 60}s
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {funnels.length === 0 && (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <p className="text-gray-600 mb-4">No conversion funnels configured</p>
          <p className="text-sm text-gray-500 mb-4">
            Create a funnel to track how visitors progress through your website and convert to customers.
          </p>
          <button
            onClick={() => {
              setEditingFunnel(null);
              setShowForm(true);
            }}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 text-sm font-medium"
          >
            Create Your First Funnel
          </button>
        </div>
      )}

      {/* Funnel Form Modal */}
      {showForm && (
        <FunnelForm
          funnel={editingFunnel}
          onSave={() => {
            setShowForm(false);
            setEditingFunnel(null);
            loadFunnels();
          }}
          onCancel={() => {
            setShowForm(false);
            setEditingFunnel(null);
          }}
        />
      )}
    </div>
  );
};

